import React, { useEffect, useMemo, useState } from "react";
import GenericTabs from "@ui-components/Tabs/GenericTabs";
import {
  GenericTable,
  type GenericTableColumn,
} from "@ui-components/Table/GenericTable";
import { SkeletonBlock } from "@ui-components/Skeleton/GenericSkeleton";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { formatBandwidthFromMbpsInline } from "../../backbone.data";
import {
  fetchBbConnectorBgp,
  fetchBbConnectorRoutes,
  formatBbConnectorServiceDetail,
  type BbConnectorListItem,
  type BbConnectorBgpResponseData,
  type BbConnectorRoutesResponseData,
} from "../../requests/request";
import { ReactComponent as IconInfo } from "../../../../../shared/assets/Icons/circle-info-solid.svg";

export interface TopologyConnectorDetailPanelProps {
  connector: BbConnectorListItem;
  onClose: () => void;
  tenantId: number | null;
}

const TABS = [
  { label: "Bandwidth & BGP", value: "bw-bgp" },
  { label: "Routing", value: "routing" },
];

function formatBgpUptime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "—";
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const parts: string[] = [];
  if (d > 0) parts.push(`${d}d`);
  if (h > 0 || d > 0) parts.push(`${h}h`);
  parts.push(`${m}m`);
  return parts.join(" ");
}

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div>
    <h4 className="text-[11px] font-semibold text-semantic-text-secondary uppercase tracking-wide mb-1">
      {title}
    </h4>
    <div className="flex flex-col gap-0.5">{children}</div>
  </div>
);

const Row: React.FC<{ label: string; value: string; mono?: boolean }> = ({
  label,
  value,
  mono,
}) => (
  <div className="flex justify-between gap-2 text-[11px] border-b border-semantic-border-primary py-1 last:border-0">
    <span className="text-semantic-text-secondary flex-shrink-0">{label}</span>
    <span
      className={`text-semantic-text-primary font-medium text-right break-all ${
        mono ? "font-mono" : ""
      }`}
    >
      {value}
    </span>
  </div>
);

const TopologyConnectorDetailPanel: React.FC<
  TopologyConnectorDetailPanelProps
> = ({ connector, onClose, tenantId }) => {
  const api = useAxiosAuth0();
  const [tab, setTab] = useState("bw-bgp");
  const [bgpLoading, setBgpLoading] = useState(false);
  const [bgpError, setBgpError] = useState<string | null>(null);
  const [bgpData, setBgpData] = useState<BbConnectorBgpResponseData | null>(
    null
  );
  const [bgpRetry, setBgpRetry] = useState(0);
  const [routesLoading, setRoutesLoading] = useState(false);
  const [routesError, setRoutesError] = useState<string | null>(null);
  const [routesData, setRoutesData] =
    useState<BbConnectorRoutesResponseData | null>(null);
  const [routesRetry, setRoutesRetry] = useState(0);

  useEffect(() => {
    setTab("bw-bgp");
  }, [connector?.id]);

  useEffect(() => {
    if (tenantId == null) {
      setBgpData(null);
      setBgpError(null);
      setBgpLoading(false);
      return;
    }
    const id = connector.id;
    let cancelled = false;
    setBgpLoading(true);
    setBgpError(null);
    fetchBbConnectorBgp(api, tenantId, id)
      .then((res) => {
        if (cancelled) return;
        const body = res.data;
        if (body?.success && body.data) {
          setBgpData(body.data);
          setBgpError(null);
        } else {
          setBgpData(null);
          setBgpError(body?.message ?? "Failed to load BGP session");
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setBgpData(null);
        setBgpError(
          err instanceof Error ? err.message : "Failed to load BGP session"
        );
      })
      .finally(() => {
        if (!cancelled) setBgpLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [connector, tenantId, api, bgpRetry]);

  useEffect(() => {
    if (tenantId == null) {
      setRoutesData(null);
      setRoutesError(null);
      setRoutesLoading(false);
      return;
    }
    const id = connector.id;
    let cancelled = false;
    setRoutesLoading(true);
    setRoutesError(null);
    fetchBbConnectorRoutes(api, tenantId, id)
      .then((res) => {
        if (cancelled) return;
        const body = res.data;
        if (body?.success && body.data) {
          setRoutesData(body.data);
          setRoutesError(null);
        } else {
          setRoutesData(null);
          setRoutesError(body?.message ?? "Failed to load routes");
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setRoutesData(null);
        setRoutesError(
          err instanceof Error ? err.message : "Failed to load routes"
        );
      })
      .finally(() => {
        if (!cancelled) setRoutesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [connector, tenantId, api, routesRetry]);

  const routeRows = useMemo(() => {
    const id = connector?.id;
    if (id == null || routesData?.connectorId !== id || !routesData.routes) {
      return [];
    }
    return routesData.routes.map((r, idx) => ({
      prefix: r.prefix,
      lp: r.lp,
      med: r.med,
      asPath: r.asPath != null && r.asPath !== "" ? r.asPath : "—",
      best: r.best,
      origin: r.origin != null && r.origin !== "" ? r.origin : "—",
      community:
        r.community != null && r.community !== "" ? r.community : "—",
      direction: r.direction != null && r.direction !== "" ? r.direction : "—",
      rowKey: `${r.prefix}|${idx}`,
    }));
  }, [connector?.id, routesData]);

  const routeColumns = useMemo<GenericTableColumn<(typeof routeRows)[0]>[]>(
    () => [
      {
        header: "Prefix",
        renderCell: (r) => (
          <span className="font-mono text-[10px] font-medium text-semantic-text-primary">
            {r.prefix}
          </span>
        ),
      },
      { header: "LP", accessor: "lp" },
      { header: "MED", accessor: "med" },
      {
        header: "AS Path",
        renderCell: (r) => (
          <span className="font-mono text-[10px] text-semantic-text-secondary break-all">
            {r.asPath}
          </span>
        ),
      },
      { header: "Origin", accessor: "origin" },
      {
        header: "Best",
        renderCell: (r) => (r.best ? "Yes" : "No"),
      },
    ],
    []
  );

  const ep = connector;
  const title =
    formatBbConnectorServiceDetail(ep.connectedServiceDetail) || ep.name;

  return (
    <div className="w-96 h-full overflow-y-auto bg-semantic-bg-primary border-l border-t border-semantic-border-primary flex flex-col">
      {/* Header */}
      <div className="flex items-start justify-between gap-2 px-4 pt-4 pb-2">
        <div>
          <h3 className="text-sm font-bold text-semantic-text-primary">
            Connector Details
          </h3>
          <p className="text-xs text-semantic-text-secondary mt-0.5 break-all">
            {title}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-semantic-text-secondary hover:text-semantic-text-primary text-lg leading-none flex-shrink-0 mt-0.5"
        >
          &times;
        </button>
      </div>

      {/* Tabs */}
      <div className="px-4 pb-2">
        <GenericTabs
          tabs={TABS}
          activeTab={tab}
          onTabChange={setTab}
          size="sm"
        />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 pb-4">
        {tab === "bw-bgp" && (
          <div className="flex flex-col gap-4">
            <Section title="Basic Information">
              <Row label="ID" value={String(ep.id)} mono />
              <Row label="Name" value={ep.name} />
              <Row label="Type" value={ep.connectedServiceType} />
              {ep.vrf?.displayName && (
                <Row label="VRF" value={ep.vrf.displayName} />
              )}
              {ep.location?.displayName && (
                <Row label="Location" value={ep.location.displayName} />
              )}
              <Row label="VLAN" value={String(ep.vlan ?? "—")} mono />
              <Row
                label="BGP neighbor"
                value={ep.bgpNeighborIp ?? "—"}
                mono
              />
            </Section>

            <Section title="Bandwidth">
              <div className="grid grid-cols-3 gap-1.5 mt-1">
                <div className="rounded-lg border border-semantic-border-primary bg-semantic-bg-secondary px-2 py-1.5 text-center">
                  <div className="text-[9px] text-semantic-text-secondary uppercase">
                    Capacity
                  </div>
                  <div className="text-xs font-bold text-semantic-text-brand">
                    {ep.capacity != null ? `${ep.capacity} Mbps` : "—"}
                  </div>
                </div>
                <div className="rounded-lg border border-semantic-border-primary bg-semantic-bg-secondary px-2 py-1.5 text-center">
                  <div className="text-[9px] text-semantic-text-secondary uppercase">
                    Inbound
                  </div>
                  <div className="text-xs font-bold text-semantic-text-brand">
                    {ep.bandwidth
                      ? formatBandwidthFromMbpsInline(ep.bandwidth.inbound)
                      : "—"}
                  </div>
                </div>
                <div className="rounded-lg border border-semantic-border-primary bg-semantic-bg-secondary px-2 py-1.5 text-center">
                  <div className="text-[9px] text-semantic-text-secondary uppercase">
                    Outbound
                  </div>
                  <div className="text-xs font-bold text-semantic-text-brand">
                    {ep.bandwidth
                      ? formatBandwidthFromMbpsInline(ep.bandwidth.outbound)
                      : "—"}
                  </div>
                </div>
              </div>
            </Section>

            <Section title="BGP Session">
              {bgpLoading && (
                <div className="space-y-2 rounded-lg border border-semantic-border-primary bg-semantic-bg-secondary p-2">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="flex justify-between gap-2">
                      <SkeletonBlock h={14} w={80} className="rounded" />
                      <SkeletonBlock h={14} w={100} className="rounded" />
                    </div>
                  ))}
                </div>
              )}
              {!bgpLoading && bgpError != null && (
                <div className="flex flex-col items-center rounded-lg border border-semantic-border-primary bg-semantic-bg-secondary px-3 py-6">
                  <IconInfo className="h-6 w-6 flex-shrink-0 text-yellow-500" />
                  <p className="mt-1.5 text-center text-[11px] text-semantic-text-secondary">
                    {bgpError}
                  </p>
                  <button
                    type="button"
                    onClick={() => setBgpRetry((t) => t + 1)}
                    className="mt-2 text-[11px] font-medium text-semantic-text-brand hover:underline"
                  >
                    Retry
                  </button>
                </div>
              )}
              {!bgpLoading &&
                bgpError == null &&
                bgpData?.connectorId === ep.id && (
                  <>
                    {(
                      [
                        ["State", bgpData.bgpSession.state ?? "—"],
                        [
                          "Established",
                          bgpData.bgpSession.established ? "Yes" : "No",
                        ],
                        ["Peer IP", bgpData.bgpSession.peerIp ?? "—"],
                        ["Local IP", bgpData.bgpSession.localIp ?? "—"],
                        ["Local ASN", String(bgpData.bgpSession.localAsn)],
                        ["Remote ASN", String(bgpData.bgpSession.remoteAsn)],
                        [
                          "Uptime",
                          formatBgpUptime(bgpData.bgpSession.uptime),
                        ],
                        [
                          "Keepalive",
                          `${bgpData.bgpSession.keepalive}s (cfg ${bgpData.bgpSession.configuredKeepalive}s)`,
                        ],
                        [
                          "Hold",
                          `${bgpData.bgpSession.hold}s (cfg ${bgpData.bgpSession.configuredHold}s)`,
                        ],
                        [
                          "Rx prefixes",
                          String(bgpData.bgpSession.rxPrefixes),
                        ],
                        [
                          "Tx prefixes",
                          String(bgpData.bgpSession.txPrefixes),
                        ],
                        ["Router", bgpData.bgpSession.router ?? "—"],
                      ] as [string, string][]
                    ).map(([label, value]) => (
                      <Row
                        key={label}
                        label={label}
                        value={value}
                        mono={[
                          "Peer IP",
                          "Local IP",
                          "Local ASN",
                          "Remote ASN",
                        ].includes(label)}
                      />
                    ))}
                  </>
                )}
            </Section>
          </div>
        )}

        {tab === "routing" && (
          <div className="flex flex-col gap-3">
            {routesData?.vrf?.displayName && (
              <p className="text-[11px] text-semantic-text-secondary">
                VRF{" "}
                <span className="font-medium">
                  {routesData.vrf.displayName}
                </span>
                {routesData.total != null && (
                  <span className="ml-2">· {routesData.total} routes</span>
                )}
              </p>
            )}
            {routesLoading && (
              <div className="space-y-2">
                <SkeletonBlock h={28} className="w-full rounded" />
                {Array.from({ length: 5 }).map((_, i) => (
                  <SkeletonBlock key={i} h={24} className="w-full rounded" />
                ))}
              </div>
            )}
            {!routesLoading && routesError != null && (
              <div className="flex flex-col items-center rounded-lg border border-semantic-border-primary bg-semantic-bg-secondary px-3 py-6">
                <IconInfo className="h-6 w-6 flex-shrink-0 text-yellow-500" />
                <p className="mt-1.5 text-center text-[11px] text-semantic-text-secondary">
                  {routesError}
                </p>
                <button
                  type="button"
                  onClick={() => setRoutesRetry((t) => t + 1)}
                  className="mt-2 text-[11px] font-medium text-semantic-text-brand hover:underline"
                >
                  Retry
                </button>
              </div>
            )}
            {!routesLoading && routesError == null && routeRows.length > 0 && (
              <div className="max-h-[55vh] overflow-auto rounded-lg border border-semantic-border-primary text-[10px]">
                <GenericTable
                  columns={routeColumns}
                  data={routeRows}
                  rowKey={(r) => r.rowKey}
                />
              </div>
            )}
            {!routesLoading &&
              routesError == null &&
              routeRows.length === 0 && (
                <p className="py-6 text-center text-[11px] text-semantic-text-secondary">
                  No routes returned for this connector.
                </p>
              )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TopologyConnectorDetailPanel;
